import React, { useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

interface EligibleSessionOption {
    sessionId: string;
    sessionTitle: string;
}

interface CampReviewComposerModalProps {
    visible: boolean;
    onClose: () => void;
    onSubmit: (payload: { sessionId: string; rating: number; comment: string }) => Promise<void>;
    eligibleSessions: EligibleSessionOption[];
    isSubmitting?: boolean;
}

const CampReviewComposerModal: React.FC<CampReviewComposerModalProps> = ({
    visible,
    onClose,
    onSubmit,
    eligibleSessions,
    isSubmitting = false,
}) => {
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [selectedSessionId, setSelectedSessionId] = useState<string>('');

    const canSubmit = useMemo(() => {
        return rating > 0 && comment.trim().length >= 20 && Boolean(selectedSessionId) && !isSubmitting;
    }, [comment, isSubmitting, rating, selectedSessionId]);

    const handleOpen = () => {
        if (!selectedSessionId && eligibleSessions.length > 0) {
            setSelectedSessionId(eligibleSessions[0].sessionId);
        }
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;
        await onSubmit({ sessionId: selectedSessionId, rating, comment: comment.trim() });
        setRating(0);
        setComment('');
        setSelectedSessionId('');
    };

    return (
        <Modal visible={visible} transparent animationType="fade" onShow={handleOpen} onRequestClose={onClose}>
            <View style={styles.overlay}>
                <View style={styles.modalCard}>
                    <View style={styles.header}>
                        <Text style={styles.title}>Write a review</Text>
                        <Pressable onPress={onClose} style={styles.closeButton}>
                            <MaterialCommunityIcons name="close" size={20} color="#333" />
                        </Pressable>
                    </View>

                    <ScrollView style={styles.body} contentContainerStyle={{ paddingBottom: 12 }}>
                        <Text style={styles.label}>Rate your experience</Text>
                        <View style={styles.starsRow}>
                            {[1, 2, 3, 4, 5].map((value) => (
                                <Pressable key={value} onPress={() => setRating(value)} style={styles.starButton}>
                                    <MaterialCommunityIcons
                                        name={value <= rating ? 'star' : 'star-outline'}
                                        size={28}
                                        color={value <= rating ? '#F5A623' : '#9CA3AF'}
                                    />
                                </Pressable>
                            ))}
                        </View>

                        <Text style={[styles.label, { marginTop: 16 }]}>Which completed session are you reviewing?</Text>
                        <View style={styles.sessionWrap}>
                            {eligibleSessions.map((session) => {
                                const selected = selectedSessionId === session.sessionId;
                                return (
                                    <Pressable
                                        key={session.sessionId}
                                        onPress={() => setSelectedSessionId(session.sessionId)}
                                        style={[styles.sessionPill, selected && styles.sessionPillSelected]}
                                    >
                                        <Text style={[styles.sessionText, selected && styles.sessionTextSelected]} numberOfLines={2}>
                                            {session.sessionTitle}
                                        </Text>
                                    </Pressable>
                                );
                            })}
                        </View>

                        <Text style={[styles.label, { marginTop: 16 }]}>Your review</Text>
                        <TextInput
                            style={styles.textInput}
                            value={comment}
                            onChangeText={setComment}
                            multiline
                            numberOfLines={5}
                            textAlignVertical="top"
                            placeholder="Share what went well, what could improve, and what families should know."
                            placeholderTextColor="#9CA3AF"
                            maxLength={1000}
                        />
                        <Text style={styles.helperText}>Minimum 20 characters. Be specific and respectful.</Text>
                    </ScrollView>

                    <View style={styles.footer}>
                        <Pressable onPress={onClose} style={styles.secondaryButton} disabled={isSubmitting}>
                            <Text style={styles.secondaryButtonText}>Cancel</Text>
                        </Pressable>
                        <Pressable
                            onPress={handleSubmit}
                            style={[styles.primaryButton, !canSubmit && styles.primaryButtonDisabled]}
                            disabled={!canSubmit}
                        >
                            <Text style={styles.primaryButtonText}>{isSubmitting ? 'Submitting...' : 'Submit review'}</Text>
                        </Pressable>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.35)',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
    },
    modalCard: {
        width: '100%',
        maxWidth: 680,
        maxHeight: '82%',
        backgroundColor: '#fff',
        borderRadius: 16,
        overflow: 'hidden',
    },
    header: {
        paddingHorizontal: 18,
        paddingVertical: 14,
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    title: {
        fontSize: 18,
        fontWeight: '700',
        color: '#111827',
    },
    closeButton: {
        padding: 4,
    },
    body: {
        paddingHorizontal: 18,
        paddingTop: 14,
    },
    label: {
        fontSize: 14,
        fontWeight: '600',
        color: '#1F2937',
    },
    starsRow: {
        flexDirection: 'row',
        marginTop: 8,
    },
    starButton: {
        marginRight: 6,
    },
    sessionWrap: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginTop: 8,
        gap: 8,
    },
    sessionPill: {
        borderWidth: 1,
        borderColor: '#D1D5DB',
        borderRadius: 999,
        paddingHorizontal: 12,
        paddingVertical: 8,
        maxWidth: '100%',
    },
    sessionPillSelected: {
        borderColor: '#2563EB',
        backgroundColor: '#EFF6FF',
    },
    sessionText: {
        fontSize: 13,
        color: '#374151',
    },
    sessionTextSelected: {
        color: '#1D4ED8',
        fontWeight: '600',
    },
    textInput: {
        marginTop: 8,
        minHeight: 120,
        borderWidth: 1,
        borderColor: '#D1D5DB',
        borderRadius: 12,
        paddingHorizontal: 12,
        paddingVertical: 10,
        fontSize: 14,
        color: '#111827',
    },
    helperText: {
        marginTop: 6,
        fontSize: 12,
        color: '#6B7280',
    },
    footer: {
        borderTopWidth: 1,
        borderTopColor: '#E5E7EB',
        paddingHorizontal: 18,
        paddingVertical: 12,
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 10,
    },
    secondaryButton: {
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#D1D5DB',
    },
    secondaryButtonText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#374151',
    },
    primaryButton: {
        backgroundColor: '#2563EB',
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 10,
    },
    primaryButtonDisabled: {
        backgroundColor: '#93C5FD',
    },
    primaryButtonText: {
        fontSize: 14,
        fontWeight: '700',
        color: 'white',
    },
});

export default CampReviewComposerModal;
